import {
  CheckCircleOutline,
  CloudUploadOutlined,
  DownloadOutlined,
  QueuePlayNext,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  Container,
  CssBaseline,
  Divider,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import clsx from 'clsx';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import SimpleAutoComplete from '../../components/auto-complete/autocomplete';
import Footer from '../../components/footer';
import HeaderMenu from '../../components/header-menu';
import CustomizedSnackbars from '../../components/material-snackbars';
import { browserHistory } from '../../helpers/history';
import Api from '../../services/api';
import { Styles } from '../../styles/material-styles';

class FaqImport extends Component {
  constructor(props) {
    super(props);

    this.state = {
      file: null,
      faqCount: null,
      subject: { id: 0, name: '' },
      subjectList: [],
      loadingSubjects: true,
      loading: false,
      isDragging: false,
      openNotification: false,
      notificationVariant: 'error',
      notificationMessage: '',
    };

    this.dragDepth = 0;

    this.changeValues = this.changeValues.bind(this);
    this.closeNotification = this.closeNotification.bind(this);
    this.importModelFile = this.importModelFile.bind(this);
    this.onChangeFile = this.onChangeFile.bind(this);
    this.selectFile = this.selectFile.bind(this);
  }

  componentDidMount() {
    this.getSubjects();
  }

  getErrorMessage(error) {
    const { intl } = this.props;
    const response = error && error.response ? error.response : error;
    const data = response && response.data ? response.data : response;

    return data && data.errors && data.errors[0]
      ? data.errors[0]
      : intl.formatMessage({ id: 'process.error' });
  }

  async getSubjects() {
    this.setState({ loadingSubjects: true });

    try {
      const response = await Api.get('/faq-subject/get-subjects');

      if (response.data.success) {
        this.setState({
          subjectList: response.data.data || [],
          loadingSubjects: false,
        });
        return;
      }

      this.setState({
        loadingSubjects: false,
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: this.getErrorMessage(response.data),
      });
      Api.kickoff(response);
    } catch (error) {
      this.setState({
        loadingSubjects: false,
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: this.getErrorMessage(error),
      });
      Api.kickoff(error);
    }
  }

  onFormSubmit = async (event) => {
    event.preventDefault();

    const { file, subject } = this.state;
    const { intl } = this.props;

    if (!subject || !subject.id) {
      this.setState({
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: intl.formatMessage({ id: 'faq.subject.required' }),
      });
      return;
    }

    if (!file) {
      this.setState({
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: intl.formatMessage({ id: 'excel' }),
      });
      return;
    }

    this.setState({ loading: true });

    const data = new FormData();
    data.append('file', file);
    data.append('subjectid', subject.id);

    try {
      const result = await Api.post('/faq/planilhas', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (result.data.success) {
        const createdCount = Number(result.data.data.createdcount);

        this.setState({
          loading: false,
          file: null,
          faqCount: null,
          openNotification: true,
          notificationVariant: 'success',
          notificationMessage: Number.isFinite(createdCount)
            ? intl.formatMessage(
              { id: 'faq.import.success' },
              { count: createdCount }
            )
            : intl.formatMessage({ id: 'add.success' }),
        });
        return;
      }

      this.setState({
        loading: false,
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: this.getErrorMessage(result.data),
      });
      Api.kickoff(result);
    } catch (error) {
      this.setState({
        loading: false,
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: this.getErrorMessage(error),
      });
      Api.kickoff(error);
    }
  };

  async importModelFile() {
    this.setState({ loading: true });

    try {
      const response = await Api.get('/faq/importfile', { responseType: 'blob' });
      saveAs(new Blob([response.data]), 'Modelo_FAQs.csv');
      this.setState({ loading: false });
    } catch (error) {
      this.setState({
        loading: false,
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: this.getErrorMessage(error),
      });
      Api.kickoff(error);
    }
  }

  onChangeFile(event) {
    const file = event.target.files[0] || null;

    this.selectFile(file);
  }

  selectFile(file) {
    const { intl } = this.props;

    if (file && !file.name.toLowerCase().endsWith('.csv')) {
      this.setState({
        file: null,
        faqCount: null,
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: intl.formatMessage({ id: 'faq.import.file.invalid' }),
      });
      return;
    }

    this.setState({ file, faqCount: null });

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const faqCount = this.countCsvFaqs(reader.result || '');

      this.setState((state) => (state.file === file ? { faqCount } : null));
    };

    reader.onerror = () => {
      this.setState((state) => (state.file === file ? { faqCount: null } : null));
    };

    reader.readAsText(file);
  }

  onDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.state.loading) {
      return;
    }

    this.dragDepth += 1;
    this.setState({ isDragging: true });
  };

  onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.state.loading) {
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.dragDepth = Math.max(this.dragDepth - 1, 0);

    if (this.dragDepth === 0) {
      this.setState({ isDragging: false });
    }
  };

  onDropFile = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.dragDepth = 0;
    this.setState({ isDragging: false });

    if (!this.state.loading) {
      this.selectFile(event.dataTransfer.files[0] || null);
    }
  };

  countCsvFaqs(content) {
    const records = [];
    let currentRecord = '';
    let insideQuotes = false;

    for (let index = 0; index < content.length; index += 1) {
      const character = content[index];

      if (character === '"') {
        if (insideQuotes && content[index + 1] === '"') {
          currentRecord += '"';
          index += 1;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (!insideQuotes && (character === '\r' || character === '\n')) {
        if (character === '\r' && content[index + 1] === '\n') {
          index += 1;
        }

        records.push(currentRecord);
        currentRecord = '';
      } else {
        currentRecord += character;
      }
    }

    if (currentRecord.length > 0) {
      records.push(currentRecord);
    }

    const populatedRecords = records.filter(
      (record) => record.replace(/[;,\s]/g, '').length > 0
    );

    return Math.max(populatedRecords.length - 1, 0);
  }

  changeValues(stateName, value) {
    this.setState({ [stateName]: value });
  }

  closeNotification() {
    this.setState({ openNotification: false });
  }

  render() {
    const { actionsHeaderMenu, classes, headerMenu, intl } = this.props;
    const { faqCount, file, isDragging, subject } = this.state;
    const canImport = Boolean(file && subject && subject.id);

    if (Api == null) {
      document.location.href = `/${this.props.match.params.lang}/unavailable`;
    }

    return (
      <div className={classes.root}>
        <HeaderMenu />
        <main
          className={clsx(
            classes.content,
            { [classes.contentShift]: headerMenu.open },
            classes.actionsContent,
            { [classes.actionscontentShift]: actionsHeaderMenu.open }
          )}
        >
          <div className={classes.drawerHeader} />

          <Container component='main' maxWidth='xl'>
            <CssBaseline />
            <Grid container spacing={6}>
              <Grid item xs={12} sm={4}>
                <Button variant='outlined' color='primary' onClick={browserHistory.goBack}>{<FormattedMessage id="back" />}</Button>
              </Grid>

              <Grid xs={12} sm={4} />
              <Grid item xs={12} sm={4} />
            </Grid>

            <Box sx={{ py: { xs: 1, md: 3 } }}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 760,
                  mx: 'auto',
                  pt: 1,
                  pb: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Avatar sx={{ bgcolor: 'secondary.main', m: 1 }}>
                  <QueuePlayNext />
                </Avatar>

                <Typography component='h1' variant='h5'>
                  {<FormattedMessage id="faq.create.lote" />}
                </Typography>
              </Box>

              <Card
                variant="outlined"
                sx={{
                  width: '100%',
                  maxWidth: 760,
                  mx: 'auto',
                  borderRadius: 3,
                  borderColor: 'divider',
                  boxShadow: '0 8px 28px rgba(0, 0, 0, 0.07)',
                  overflow: 'visible',
                }}
              >
                <Box
                  component="form"
                  name="faqImportForm"
                  onSubmit={this.onFormSubmit}
                  sx={{ px: { xs: 3, sm: 5 }, py: 4 }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography color="text.secondary" sx={{ mb: 1 }}>
                        <FormattedMessage id="faq.import.subtitle" />
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          1
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={600}>
                            <FormattedMessage id="faq.import.model.title" />
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <FormattedMessage id="faq.import.model.help" />
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                              type="button"
                              variant="outlined"
                              color="primary"
                              startIcon={<DownloadOutlined />}
                              onClick={this.importModelFile}
                              disabled={this.state.loading}
                              sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                              <FormattedMessage id="import.plan" />
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          2
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={600}>
                            <FormattedMessage id="faq.import.subject.title" />
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <FormattedMessage id="faq.import.subject.help" />
                          </Typography>
                          {this.state.loadingSubjects ? (
                            <Skeleton
                              variant="rounded"
                              width="100%"
                              height={56}
                              animation="wave"
                            />
                          ) : (
                            <SimpleAutoComplete
                              label={intl.formatMessage({ id: 'faq.subject' })}
                              options={this.state.subjectList}
                              stateName="subject"
                              changeSelect={this.changeValues}
                              selected={subject}
                              required
                            />
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          3
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={600}>
                            <FormattedMessage id="faq.import.file.title" />
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <FormattedMessage id="faq.import.file.help" />
                          </Typography>

                          <Box
                            onDragEnter={this.onDragEnter}
                            onDragOver={this.onDragOver}
                            onDragLeave={this.onDragLeave}
                            onDrop={this.onDropFile}
                            sx={{
                              border: '1.5px dashed',
                              borderColor: isDragging
                                ? 'primary.main'
                                : file
                                  ? 'success.main'
                                  : 'divider',
                              bgcolor: isDragging
                                ? 'action.hover'
                                : file
                                  ? 'rgba(76, 175, 80, 0.06)'
                                  : 'background.default',
                              borderRadius: 2,
                              px: 3,
                              py: 3,
                              textAlign: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transform: isDragging ? 'scale(1.01)' : 'none',
                              transition: 'border-color 0.2s, background-color 0.2s, transform 0.2s',
                            }}
                          >
                            <input
                              key={file ? file.name : 'empty'}
                              accept=".csv,text/csv"
                              id="faq-import-file"
                              type="file"
                              onChange={this.onChangeFile}
                              style={{ display: 'none' }}
                            />
                            {file ? (
                              <React.Fragment>
                                <CheckCircleOutline color="success" sx={{ fontSize: 38, mb: 1 }} />
                                <Typography fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {(file.size / 1024).toFixed(1)} KB
                                </Typography>
                                {faqCount !== null && (
                                  <Typography
                                    variant="body2"
                                    color="success.main"
                                    fontWeight={600}
                                    sx={{ mt: 0.5, mb: 1.5 }}
                                  >
                                    <FormattedMessage
                                      id="faq.import.file.count"
                                      values={{ count: faqCount }}
                                    />
                                  </Typography>
                                )}
                              </React.Fragment>
                            ) : (
                              <React.Fragment>
                                <CloudUploadOutlined
                                  color={isDragging ? 'primary' : 'action'}
                                  sx={{ fontSize: 42, mb: 1 }}
                                />
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                  <FormattedMessage id="faq.import.file.drop" />
                                </Typography>
                              </React.Fragment>
                            )}
                            <label htmlFor="faq-import-file" style={{ display: 'block' }}>
                              <Button
                                component="span"
                                variant={file ? 'text' : 'outlined'}
                                color="primary"
                                startIcon={<CloudUploadOutlined />}
                                disabled={this.state.loading}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                <FormattedMessage
                                  id={file ? 'faq.import.file.change' : 'faq.import.file.select'}
                                />
                              </Button>
                            </label>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              <FormattedMessage id="faq.import.file.formats" />
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={!canImport || this.state.loading}
                        startIcon={<CloudUploadOutlined />}
                        sx={{ mt: 1, py: 1.25, textTransform: 'none', fontWeight: 700 }}
                      >
                        <FormattedMessage id="import.plan.xls" />
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </Box>

            <CustomizedSnackbars
              variant={this.state.notificationVariant}
              message={this.state.notificationMessage}
              isOpen={this.state.openNotification}
              toClose={this.closeNotification}
            />
          </Container>
        </main>
        <Footer />
      </div>
    );
  }
}

FaqImport.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession,
});

export default injectIntl(connect(mapStateToProps)(withStyles(Styles)(FaqImport)));
